import React, { createContext, useContext, useReducer, useState } from "react";
import { trackAddToCart } from "../lib/tracking";

export type CartItem = {
  /** Woo: numeriskt produkt-id · Pulpo: Core product id (prd_…) */
  id: number | string;
  name: string;
  price: number;
  quantity: number;
  img?: string;
  variants?: string[];
  /** SKU — krävs för Pulpo Flow-ordrar */
  sku?: string;
  /** Om false eller undefined anses produkten inte vara i lager (t.ex. för express-frakt). */
  in_stock?: boolean;
  /** WooCommerce stock_status, t.ex. "instock" – används som fallback om in_stock saknas. */
  stock_status?: string;
  /** WooCommerce shipping_class, t.ex. "frakt" eller "shipping-light". */
  shipping_class?: string;
  /** Core: produkten får inte plats i paketbox — dölj parcel_locker i TMA. */
  no_parcel_locker?: boolean;
};

/** WooCommerce `stock_status` — köpbar men ej fysiskt i lager just nu. */
export function cartItemIsOnBackorder(item: Pick<CartItem, "stock_status">): boolean {
  return item.stock_status === "onbackorder";
}

type Coupon = {
  code: string;
  type: "percent" | "fixed_cart";
  amount: number;
};

type AddToCartOptions = {
  /** Flera addToCart i rad från paketsidan – nollställer inte paket-fee förrän setPackageFeeTotal anropas efteråt */
  isPackageBatch?: boolean;
};

type CartContextType = {
  cart: CartItem[];
  cartItemCount: number;
  couponCode?: string;
  /** Justering mot listpris: negativt = paketrabatt, positivt = tillägg. Skickas som Woo fee line. */
  packageFeeTotal: number;
  /** Sant efter att energipaket lagts i varukorgen (även om paketavgift är 0). Rabattkoder gäller då inte. */
  packageOrderActive: boolean;
  setPackageFeeTotal: (amount: number) => void;
  addToCart: (item: CartItem, options?: AddToCartOptions) => void;
  removeFromCart: (id: number | string) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  /** Varor efter paketrabatt, före kupong */
  getMerchandiseAfterPackage: () => number;
  getFinalPrice: () => number;
  updateItemQuantity: (id: number | string, quantity: number) => void;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => void;
  getTotalDiscountAmount: () => number;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartItem[], action: any) => {
  switch (action.type) {
    case "ADD_TO_CART":
      const existingItem = state.find((item) => item.id === action.payload.id);
      if (existingItem) {
        return state.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                quantity: item.quantity + (action.payload.quantity ?? 1),
                in_stock: action.payload.in_stock ?? item.in_stock,
                stock_status: action.payload.stock_status ?? item.stock_status,
                shipping_class: action.payload.shipping_class ?? item.shipping_class,
                no_parcel_locker:
                  action.payload.no_parcel_locker ?? item.no_parcel_locker,
              }
            : item
        );
      }
      return [...state, { ...action.payload, quantity: action.payload.quantity ?? 1 }];

    case "UPDATE_QUANTITY":
      return state.map((item) =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );

    case "REMOVE_FROM_CART":
      return state.filter((item) => item.id !== action.payload.id);

    case "CLEAR_CART":
      return [];

    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cart, dispatch] = useReducer(cartReducer, []);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [packageFeeTotal, setPackageFeeTotalState] = useState(0);
  const [packageOrderActive, setPackageOrderActive] = useState(false);

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  const clearPackageOrderState = () => {
    setPackageFeeTotalState(0);
    setPackageOrderActive(false);
  };

  const setPackageFeeTotal = (amount: number) => {
    setPackageFeeTotalState(amount);
    setPackageOrderActive(true);
    setCoupon(null);
  };

  const addToCart = (item: CartItem, options?: AddToCartOptions) => {
    if (!options?.isPackageBatch) {
      clearPackageOrderState();
    }
    dispatch({ type: "ADD_TO_CART", payload: item });
    trackAddToCart(item);
  };

  const removeFromCart = (id: number | string) => {
    clearPackageOrderState();
    dispatch({ type: "REMOVE_FROM_CART", payload: { id } });
  };

  const clearCart = () => {
    dispatch({ type: "CLEAR_CART" });
    setCoupon(null);
    clearPackageOrderState();
  };

  const updateItemQuantity = (id: number | string, quantity: number) => {
    clearPackageOrderState();
    if (quantity < 1) {
      dispatch({ type: "REMOVE_FROM_CART", payload: { id } });
    } else {
      dispatch({ type: "UPDATE_QUANTITY", payload: { id, quantity } });
    }
  };

  // 🔵 Subtotal (listpris)
  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getMerchandiseAfterPackage = () => {
    return Math.max(0, getTotalPrice() + packageFeeTotal);
  };

  // 🔵 Kupongrabatt (räknas på belopp efter paketrabatt; ej vid energipaket)
  const getTotalDiscountAmount = () => {
    if (!coupon || packageOrderActive) return 0;

    const subtotal = getMerchandiseAfterPackage();
    let discount = 0;

    if (coupon.type === "percent") {
      discount = (subtotal * coupon.amount) / 100;
    }

    if (coupon.type === "fixed_cart") {
      discount = coupon.amount;
    }

    return Math.min(discount, subtotal);
  };

  // 🔵 Slutpris varor (före frakt)
  const getFinalPrice = () => {
    return getMerchandiseAfterPackage() - getTotalDiscountAmount();
  };

  // 🔵 Hämta och spara kupongregler (inte uträknat värde)
  const applyCoupon = async (code: string) => {
    if (packageOrderActive) {
      throw new Error(
        "Rabattkod kan inte användas tillsammans med energipaket — paketpriset är redan rabatterat."
      );
    }
    const response = await fetch(`/api/coupons/${code}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Ogiltig rabattkod");
    }

    setCoupon({
      code,
      type: data.discount_type,
      amount: parseFloat(data.amount),
    });
  };

  const removeCoupon = () => {
    setCoupon(null);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartItemCount,
        couponCode: coupon?.code,
        packageFeeTotal,
        packageOrderActive,
        setPackageFeeTotal,
        addToCart,
        removeFromCart,
        clearCart,
        getTotalPrice,
        getMerchandiseAfterPackage,
        getFinalPrice,
        updateItemQuantity,
        applyCoupon,
        removeCoupon,
        getTotalDiscountAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart måste användas inom en CartProvider");
  }
  return context;
};
